/**
 * In-place facet filtering for collection pages and the ring finder.
 *
 * Liquid renders the whole shelf with data-f-* attributes and the facet
 * controls with their unfiltered counts; this keeps them honest as filters
 * change: OR within a facet, AND across facets, counts in models (distinct
 * families) computed against the OTHER active facets, so a control says
 * what clicking it would leave. A value with nothing left is aria-disabled,
 * never removed from the tab order. The URL stays in sync, so filtered
 * views can be linked and shared.
 *
 * Families: the card Liquid shows first (its representative, e.g. the
 * Black bag of each size) stays while it matches; otherwise the first
 * matching member steps in, so ?color=blue swaps every size to Blue and
 * ?shape=hexagon swaps every cage footprint to a hexagon. A card with
 * data-line-parts ("shape~Octagon or hexagon|platform~1, 2 or 3ft
 * platform") rewrites its variant line from the single active value of
 * each named facet.
 *
 * Also owns the toolbar: "Filters (n)", the live count, the sheet's
 * "Show N" button and status line, focus after a chip goes, and sort.
 */
(() => {
  const root = document.querySelector('[data-pl-facets]');
  if (!root) return;

  const html = document.documentElement;
  const grid = root.querySelector('[data-facets-grid]');
  const cards = grid ? Array.from(grid.querySelectorAll(':scope > .pl-card')) : [];
  const chips = Array.from(root.querySelectorAll('[data-facet][data-value]'));
  const applied = root.querySelector('[data-facets-applied]');
  const empty = root.querySelector('[data-facets-empty]');
  const counter = root.querySelector('[data-facets-count]');
  const status = root.querySelector('[data-facets-status]');
  const trigger = root.querySelector('[data-pl-open="filters"]');
  const showButtons = Array.from(root.querySelectorAll('[data-facets-show]'));
  const activeBadges = Array.from(root.querySelectorAll('[data-facets-active-n]'));
  const keys = Array.from(new Set(chips.map((chip) => chip.dataset.facet)));
  const single = new Set((root.dataset.single || '').split(',').filter(Boolean));
  const noun = root.dataset.noun || 'product';
  const line = root.dataset.line === 'true';
  const serverCount = counter ? counter.textContent : '';
  // the count is the last place focus can land after a chip goes
  if (counter && !counter.hasAttribute('tabindex')) counter.tabIndex = -1;

  const entries = cards.map((card, index) => {
    const values = {};
    for (const key of keys) {
      if (key === 'price') continue;
      const raw = card.getAttribute(`data-f-${key}`);
      values[key] = raw ? raw.toLowerCase().split(',').filter(Boolean) : [];
    }
    return {
      card,
      values,
      price: parseFloat(card.dataset.fPrice || '0'),
      model: card.dataset.group || `#${index}`,
      rep: !card.hidden,
      color: (card.dataset.fColor || '').toLowerCase(),
    };
  });

  const params = new URLSearchParams(window.location.search);
  const state = {};
  for (const key of keys) {
    const values = params
      .getAll(key)
      .flatMap((value) => value.split(','))
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);
    state[key] = new Set(single.has(key) || key === 'price' ? values.slice(0, 1) : values);
  }

  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const shown = (el) => !!el && el.isConnected && !el.closest('[hidden]') && el.getClientRects().length > 0;

  function inRange(price, range) {
    const [min, max] = range.split('-');
    const low = parseFloat(min) || 0;
    const high = max === undefined || max === '' ? Infinity : parseFloat(max);
    return price >= low && price < high;
  }

  function has(entry, key, value) {
    return key === 'price' ? inRange(entry.price, value) : entry.values[key].includes(value);
  }

  function matches(entry, except) {
    for (const key of keys) {
      if (key === except) continue;
      const active = state[key];
      if (!active.size) continue;
      if (!Array.from(active).some((value) => has(entry, key, value))) return false;
    }
    return true;
  }

  function chipFor(key, value) {
    return chips.find((c) => c.dataset.facet === key && c.dataset.value.toLowerCase() === value);
  }

  function chipLabel(chip) {
    if (chip.dataset.label) return chip.dataset.label;
    const text = chip.firstChild && chip.firstChild.nodeType === 3 ? chip.firstChild.textContent : chip.textContent;
    return text.trim();
  }

  function escapeHtml(value) {
    return value.replace(/[&<>"']/g, (c) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'})[c]);
  }

  function syncUrl() {
    const url = new URL(window.location.href);
    for (const key of keys) {
      url.searchParams.delete(key);
      if (state[key].size) url.searchParams.set(key, Array.from(state[key]).join(','));
    }
    history.replaceState(history.state, '', url);
    for (const link of root.querySelectorAll('[data-carry-base]')) {
      const carry = new URLSearchParams();
      for (const key of (link.dataset.carryKeys || 'size,platform').split(',')) {
        if (state[key] && state[key].size) carry.set(key, Array.from(state[key]).join(','));
      }
      const base = link.dataset.carryBase;
      const query = carry.toString();
      link.href = query ? `${base}${base.includes('?') ? '&' : '?'}${query}` : base;
      const suffix = link.querySelector('[data-carry-suffix]');
      if (suffix) suffix.hidden = !query;
    }
  }

  /* A family card names what the filters leave open: one active shape
     reads "Hexagon", none or several keep the family's own words. */
  function syncLine(entry) {
    const parts = entry.card.dataset.lineParts;
    if (!parts || entry.card.hidden) return;
    const text = parts
      .split('|')
      .map((part) => {
        const [key, fallback] = part.split('~');
        const set = state[key];
        const chip = set && set.size === 1 ? chipFor(key, Array.from(set)[0]) : null;
        return chip ? chipLabel(chip) : fallback;
      })
      .join(' · ');
    const variant = entry.card.querySelector('.pl-card__variant');
    if (!variant || variant.textContent === text) return;
    variant.textContent = text;
    const link = entry.card.querySelector('.pl-card__link');
    const title = entry.card.querySelector('.pl-card__title');
    if (link && title) link.setAttribute('aria-label', `${title.textContent.trim()}, ${text}`);
  }

  function apply(initial) {
    // one card per model: the representative while it matches, else the first match
    const pick = new Map();
    const colors = new Set();
    for (const entry of entries) {
      if (!matches(entry)) continue;
      if (entry.color) colors.add(entry.color);
      const current = pick.get(entry.model);
      if (!current || (entry.rep && !current.rep)) pick.set(entry.model, entry);
    }
    for (const entry of entries) entry.card.hidden = pick.get(entry.model) !== entry;
    for (const entry of entries) syncLine(entry);
    const models = pick.size;

    for (const chip of chips) {
      const key = chip.dataset.facet;
      const value = chip.dataset.value.toLowerCase();
      const on = state[key].has(value);
      chip.classList.toggle('is-on', on);
      chip.setAttribute('aria-pressed', String(on));
      const hits = new Set();
      for (const entry of entries) {
        if (has(entry, key, value) && matches(entry, key)) hits.add(entry.model);
      }
      const badge = chip.querySelector('[data-facet-n], .pl-facetchip__n');
      if (badge) badge.textContent = hits.size;
      // aria-disabled, not disabled: the value stays focusable and says why
      chip.disabled = false;
      if (!on && hits.size === 0) chip.setAttribute('aria-disabled', 'true');
      else chip.removeAttribute('aria-disabled');
    }

    const active = [];
    for (const key of keys) {
      for (const value of state[key]) {
        const chip = chipFor(key, value);
        active.push({key, value, label: chip ? chipLabel(chip) : value});
      }
    }

    if (applied) {
      applied.hidden = active.length === 0;
      applied.innerHTML = active.length
        ? active
            .map(
              (item) =>
                `<button type="button" class="pl-applied__chip" data-facet-remove="${escapeHtml(item.key)}" data-value="${escapeHtml(item.value)}">${escapeHtml(item.label)}<span aria-hidden="true">×</span><span class="pl-vh">, remove filter</span></button>`,
            )
            .join('') + '<button type="button" class="pl-applied__clear reset" data-facets-clear>Clear all</button>'
        : '';
    }

    // line shelves count sizes and colours; with nothing active the
    // server's own count stands (/collections/all states its full total)
    const summary = line ? `${plural(models, 'size')} · ${plural(colors.size, 'color')}` : plural(models, noun);
    if (counter) counter.textContent = active.length ? summary : serverCount;
    // the sheet's status line speaks while the rest of the page is inert;
    // nothing is announced for the filters a page loads with
    if (status && !initial) status.textContent = summary;
    for (const button of showButtons) {
      button.textContent = models ? `Show ${plural(models, noun === 'product' ? 'product' : 'model')}` : 'No matches';
    }
    for (const badge of activeBadges) {
      badge.textContent = active.length ? ` (${active.length})` : '';
      badge.hidden = active.length === 0;
    }
    for (const clear of root.querySelectorAll('[data-facets-clear-inline]')) clear.hidden = active.length === 0;
    if (empty) empty.hidden = models > 0;
    if (grid) grid.hidden = models === 0;

    syncUrl();
    html.classList.remove('pl-facets-pending');
  }

  /* After a chip or "Clear all" disappears, focus lands on the chip that
     took its place, else the one before it, else the Filters button, else
     the count (tabindex -1): never on the page itself. */
  function refocus(index) {
    const now = applied ? Array.from(applied.querySelectorAll('[data-facet-remove]')) : [];
    const target = [now[index], now[index - 1], trigger, counter].find(shown);
    if (target) target.focus({preventScroll: true});
  }

  root.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-facet][data-value]');
    if (chip && !chip.hasAttribute('data-facet-remove') && root.contains(chip)) {
      const key = chip.dataset.facet;
      const value = chip.dataset.value.toLowerCase();
      const set = state[key];
      if (set.has(value)) set.delete(value);
      else {
        if (chip.getAttribute('aria-disabled') === 'true') return;
        if (key === 'price' || single.has(key)) set.clear();
        set.add(value);
      }
      apply();
      return;
    }
    const remove = event.target.closest('[data-facet-remove]');
    if (remove) {
      const index = Array.from(applied.querySelectorAll('[data-facet-remove]')).indexOf(remove);
      state[remove.dataset.facetRemove].delete(remove.dataset.value);
      apply();
      refocus(index);
      return;
    }
    const clear = event.target.closest('[data-facets-clear], [data-facets-clear-inline]');
    if (clear) {
      event.preventDefault();
      for (const key of keys) state[key].clear();
      apply();
      // the sheet's Clear stays put; "Clear all" and the empty state's button go
      if (!shown(clear)) refocus(0);
    }
  });

  for (const sort of root.querySelectorAll('[data-pl-sort] select')) {
    sort.addEventListener('change', () => {
      const url = new URL(window.location.href);
      // '' is the shelf's own order (size order on a product line)
      if (sort.value) url.searchParams.set('sort_by', sort.value);
      else url.searchParams.delete('sort_by');
      url.searchParams.delete('page');
      window.location.assign(url);
    });
  }

  /* The filters are a sheet at every width (a dialog), unless the shelf is
     big enough for a desktop rail: then it is a plain region from 990px,
     and crossing into that layout closes an open sheet (its scroll lock
     would otherwise outlive it). */
  const desktop = window.matchMedia('(min-width: 990px)');
  const drawer = root.querySelector('#pl-filters');
  const sheet = drawer && drawer.querySelector('aside');
  const hasRail = root.classList.contains('has-rail');

  function syncSheet() {
    if (!sheet || !hasRail) return;
    if (desktop.matches) {
      sheet.removeAttribute('role');
      sheet.removeAttribute('aria-modal');
    } else {
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-modal', 'true');
    }
  }

  desktop.addEventListener('change', () => {
    if (hasRail && desktop.matches && drawer && drawer.classList.contains('is-open') && window.PL && PL.closeDrawers) {
      PL.closeDrawers(false);
    }
    syncSheet();
  });

  syncSheet();
  apply(true);
  // live only after the first apply, so a filtered URL loads silently
  if (counter) counter.setAttribute('aria-live', 'polite');
})();
